import * as React from 'react';
import { Card, CardContent } from '../../../../../../components/ui/Card';
import { Button } from '../../../../../../components/ui/Button';
import { Input } from '../../../../../../components/ui/FormField';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../../../../lib/locale';
import { formatMoney } from '@repo/utils';
import type { BidDetail } from '../../../../../../features/auctions/types';

interface AuctionBidFormProps {
  subscribers: { id: string; customerId: string }[];
  bidSubscriberId: string;
  setBidSubscriberId: (v: string) => void;
  bidDiscountRupees: string;
  setBidDiscountRupees: (v: string) => void;
  onSubmitBid: (e: React.FormEvent) => void;
  isSubmittingBid: boolean;
  currentLowestBid: BidDetail | null;
  bidCount: number;
}

function formatPaise(paise: number): string {
  return formatMoney(paise, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE });
}

export function AuctionBidForm({
  subscribers,
  bidSubscriberId,
  setBidSubscriberId,
  bidDiscountRupees,
  setBidDiscountRupees,
  onSubmitBid,
  isSubmittingBid,
  currentLowestBid,
  bidCount,
}: AuctionBidFormProps) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-text-primary">Submit Bid</h2>
            <span className="text-xs text-text-muted">({bidCount} bid{bidCount === 1 ? '' : 's'} so far)</span>
          </div>
          {currentLowestBid && (
            <div className="text-xs text-text-secondary bg-surface-2 px-3 py-1.5 rounded-full border border-border">
              Lowest Bid: <span className="font-semibold text-text-primary font-mono">{formatPaise(currentLowestBid.bidDiscount)}</span>
            </div>
          )}
        </div>
        <form onSubmit={onSubmitBid} className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-48">
            <label className="block text-sm font-medium text-text-primary mb-1">Subscriber</label>
            <select
              className="w-full border border-border rounded px-3 py-2 text-sm bg-surface-1 text-text-primary"
              value={bidSubscriberId}
              onChange={(e) => setBidSubscriberId(e.target.value)}
              required
            >
              <option value="">Select subscriber…</option>
              {subscribers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.id.slice(0, 8)}…
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-36">
            <label className="block text-sm font-medium text-text-primary mb-1">Discount Bid (₹)</label>
            <Input
              type="number"
              min="0"
              step="1"
              value={bidDiscountRupees}
              onChange={(e) => setBidDiscountRupees(e.target.value)}
              placeholder={currentLowestBid ? `Beat ${(currentLowestBid.bidDiscount / 100).toFixed(0)}` : 'e.g. 5000'}
              required
            />
          </div>
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmittingBid || !bidSubscriberId || !bidDiscountRupees}
            loading={isSubmittingBid}
          >
            {isSubmittingBid ? 'Submitting…' : 'Submit Bid'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
